<template>
  <span :class="chipClasses" @click="$emit('click', $event)">
    <span v-if="$slots.prepend" class="chip__prepend">
      <slot name="prepend" />
    </span>
    <span class="chip__content">
      <slot />
    </span>
    <button v-if="closable" type="button" class="chip__close" @click.stop="$emit('close')">
      <svg viewBox="0 0 24 24" width="16" height="16">
        <path fill="currentColor" d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
      </svg>
    </button>
  </span>
</template>

<script>
export default {
  name: 'BaseChip',
  props: {
    color: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'primary', 'success', 'danger', 'warning', 'info'].includes(v)
    },
    variant: {
      type: String,
      default: 'filled',
      validator: (v) => ['filled', 'outlined', 'tonal'].includes(v)
    },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v)
    },
    closable: {
      type: Boolean,
      default: false
    },
    clickable: {
      type: Boolean,
      default: false
    }
  },
  emits: ['click', 'close'],
  computed: {
    chipClasses() {
      return [
        'chip',
        `chip--${this.color}`,
        `chip--${this.variant}`,
        `chip--${this.size}`,
        {
          'chip--clickable': this.clickable
        }
      ]
    }
  }
}
</script>

<style lang="scss" scoped>
@use '../../scss/variables' as *;

.chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border-radius: $radius-full;
  font-weight: $font-weight-medium;
  transition: all $transition-fast;

  // Sizes
  &--sm {
    height: 24px;
    padding: 0 8px;
    font-size: $font-size-xs;
  }

  &--md {
    height: 28px;
    padding: 0 10px;
    font-size: $font-size-sm;
  }

  &--lg {
    height: 32px;
    padding: 0 12px;
    font-size: $font-size-base;
  }

  // Filled variants
  &--filled {
    &.chip--default {
      background-color: $color-gray-200;
      color: $color-text-primary;
    }
    &.chip--primary {
      background-color: $color-primary;
      color: $color-text-inverse;
    }
    &.chip--success {
      background-color: $color-success;
      color: $color-text-inverse;
    }
    &.chip--danger {
      background-color: $color-danger;
      color: $color-text-inverse;
    }
    &.chip--warning {
      background-color: $color-warning;
      color: $color-text-inverse;
    }
    &.chip--info {
      background-color: $color-info;
      color: $color-text-inverse;
    }
  }

  // Outlined variants
  &--outlined {
    background-color: transparent;
    border: 1px solid currentColor;
    &.chip--default {
      color: $color-gray-600;
    }
    &.chip--primary {
      color: $color-primary;
    }
    &.chip--success {
      color: $color-success;
    }
    &.chip--danger {
      color: $color-danger;
    }
    &.chip--warning {
      color: $color-warning;
    }
    &.chip--info {
      color: $color-info;
    }
  }

  // Tonal variants
  &--tonal {
    &.chip--default {
      background-color: $color-gray-100;
      color: $color-gray-700;
    }
    &.chip--primary {
      background-color: $color-primary-50;
      color: $color-primary;
    }
    &.chip--success {
      background-color: rgba($color-success, 0.1);
      color: $color-success-dark;
    }
    &.chip--danger {
      background-color: rgba($color-danger, 0.1);
      color: $color-danger-dark;
    }
    &.chip--warning {
      background-color: rgba($color-warning, 0.1);
      color: $color-warning-dark;
    }
    &.chip--info {
      background-color: rgba($color-info, 0.1);
      color: $color-info-dark;
    }
  }

  &--clickable {
    cursor: pointer;
    &:hover {
      filter: brightness(0.95);
    }
  }

  &__prepend {
    display: flex;
    align-items: center;
  }

  &__content {
    white-space: nowrap;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0;
    margin-left: 2px;
    border: none;
    background: transparent;
    cursor: pointer;
    opacity: 0.7;
    color: inherit;

    &:hover {
      opacity: 1;
    }
  }
}
</style>
